import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Slider } from '../../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  FileEdit,
  GraduationCap,
  Plane,
  Globe,
  Home,
  DollarSign,
  Briefcase,
  ClipboardList,
  Eye,
  ListChecks,
  BookOpen,
  Calendar,
  MapPin,
  Users,
  FileText,
  Shield,
  Sparkles,
  ArrowLeftRight,
  Phone,
  X,
} from 'lucide-react';

// ─── Constants & Config ─────────────────────────────────────────────────────

const SERVICE_ACCENT = {
  'preapplication-support': '#14B8A6',
  'apply-university': '#8B5CF6',
  'visa-interview': '#F43F5E',
  'ticket-travel': '#F59E0B',
  accommodation: '#06B6D4',
  'education-loan': '#84CC16',
  'jobs-abroad': '#F97316',
};

const SUB_SERVICE_OPTIONS = [
  {
    key: 'IELTS_REGISTRATION',
    label: 'IELTS Registration',
    icon: ClipboardList,
  },
  { key: 'PTE_REGISTRATION', label: 'PTE Registration', icon: ClipboardList },
  { key: 'IELTS_CLASS', label: 'IELTS Class', icon: BookOpen },
  { key: 'PTE_CLASS', label: 'PTE Class', icon: BookOpen },
  { key: 'LANGUAGE_CLASS', label: 'Language Class', icon: Globe },
  { key: 'SOP', label: 'Statement of Purpose', icon: FileEdit },
  { key: 'LOR', label: 'Letter of Recommendation', icon: FileText },
  {
    key: 'RESEARCH_APPLICATION',
    label: 'Research Application',
    icon: GraduationCap,
  },
  { key: 'FREE_RESOURCE', label: 'Free Resource', icon: BookOpen },
  {
    key: 'COUNSELOR_APPOINTMENT',
    label: 'Counselor Appointment',
    icon: Calendar,
  },
];

const TEST_CENTERS_BD = [
  'Dhaka - British Council',
  'Dhaka - IDP',
  'Chittagong - British Council',
  'Chittagong - IDP',
  'Sylhet - British Council',
  'Rajshahi - British Council',
];

const PTE_CENTERS_BD = ['Dhaka - Pearson VUE', 'Chittagong - Pearson VUE'];

const COUNTRY_OPTIONS = [
  'USA',
  'UK',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Netherlands',
  'Sweden',
  'Ireland',
  'Switzerland',
  'New Zealand',
  'Singapore',
  'Japan',
  'South Korea',
  'Malaysia',
];

const VISA_TYPES = [
  { value: 'STUDENT_VISA', label: 'Student Visa' },
  { value: 'DEPENDENT_VISA', label: 'Dependent Visa' },
  { value: 'WORK_VISA', label: 'Work Visa' },
  { value: 'TOURIST_VISA', label: 'Tourist Visa' },
];

const ACCOMMODATION_TYPES = [
  { value: 'UNIVERSITY_HOUSING', label: 'University Housing' },
  { value: 'PRIVATE_RENTAL', label: 'Private Rental' },
  { value: 'HOMESTAY', label: 'Homestay' },
  { value: 'NO_PREFERENCE', label: 'No Preference' },
];

const JOB_TYPES = [
  { value: 'PART_TIME_DURING_STUDIES', label: 'Part-time During Studies' },
  { value: 'FULL_TIME_POST_GRADUATION', label: 'Full-time Post Graduation' },
  { value: 'INTERNSHIP', label: 'Internship' },
  { value: 'CO_OP', label: 'Co-op' },
  { value: 'FREELANCE', label: 'Freelance' },
];

const DOC_STATUS_OPTIONS = [
  { value: 'ready', label: 'Ready' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'not_started', label: 'Not Started' },
];

// ─── Shared helpers ──────────────────────────────────────────────────────────

const inputCls = hasError =>
  `bg-white/70 border transition-all duration-200 ${
    hasError
      ? 'border-red-400'
      : 'border-white/60 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100'
  }`;

const glassCard = {
  background:
    'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.45) 100%)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow:
    '0 2px 14px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
};

function stepHeaderIcon(accent) {
  return {
    background: `linear-gradient(135deg, ${accent} 0%, ${accent}bb 100%)`,
    boxShadow: `0 6px 22px ${accent}44`,
  };
}

// ─── Stepper ────────────────────────────────────────────────────────────────

function FormStepper({ steps, currentStep, accent }) {
  const totalSteps = steps.length;
  const useCompact = totalSteps > 5;

  if (useCompact) {
    const currentLabel = steps[currentStep]?.label || '';
    const Icon = steps[currentStep]?.icon || CheckCircle;
    const progress =
      totalSteps > 1 ? (currentStep / (totalSteps - 1)) * 100 : 0;

    return (
      <div className="w-full px-1 sm:px-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(accent)}
            >
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p
                className="text-xs font-medium"
                style={{ color: `${accent}99` }}
              >
                Step {currentStep + 1} of {totalSteps}
              </p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                {currentLabel}
              </p>
            </div>
          </div>
          <span
            className="text-xs font-bold tabular-nums"
            style={{ color: accent }}
          >
            {Math.round(progress)}%
          </span>
        </div>
        <div
          className="h-[3px] w-full rounded-full overflow-hidden"
          style={{ background: `${accent}18` }}
        >
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${accent}aa, ${accent})`,
            }}
          />
        </div>
      </div>
    );
  }

  // Standard icon stepper (5 or fewer steps)
  return (
    <div className="flex items-center justify-between w-full px-1 sm:px-4">
      {steps.map((step, idx) => {
        const Icon = step.icon;
        const isCompleted = idx < currentStep;
        const isActive = currentStep === idx;
        const lineFilled = idx <= currentStep;

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div
                className="flex-1 h-[2px] mx-1 sm:mx-2 rounded-full overflow-hidden"
                style={{ background: `${accent}18` }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: lineFilled ? '100%' : '0%',
                    background: `linear-gradient(90deg, ${accent}99, ${accent})`,
                  }}
                />
              </div>
            )}
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                style={
                  isActive
                    ? {
                        background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
                        color: 'white',
                        boxShadow: `0 4px 16px ${accent}44`,
                      }
                    : isCompleted
                      ? {
                          background: `${accent}14`,
                          color: accent,
                        }
                      : {
                          background: 'rgba(99,102,241,0.07)',
                          color: '#9CA3AF',
                        }
                }
              >
                {isCompleted ? (
                  <CheckCircle className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                ) : (
                  <Icon className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                )}
              </div>
              <span
                className="text-[10px] sm:text-xs font-semibold transition-colors duration-300 text-center max-w-[60px] sm:max-w-none truncate"
                style={{ color: isActive || isCompleted ? accent : '#9CA3AF' }}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Field Error ────────────────────────────────────────────────────────────

function FieldError({ error }) {
  if (!error) return null;
  return <p className="text-xs text-red-500 mt-1">{error}</p>;
}

// ─── Helper: build dynamic steps for preapplication ─────────────────────────

function buildPreappSteps(selectedSubs) {
  const base = [{ key: 'select', label: 'Select Services', icon: ListChecks }];
  selectedSubs.forEach(key => {
    const opt = SUB_SERVICE_OPTIONS.find(o => o.key === key);
    if (opt) base.push({ key, label: opt.label, icon: opt.icon });
  });
  base.push({ key: 'review', label: 'Review', icon: Eye });
  return base;
}

// ═════════════════════════════════════════════════════════════════════════════
// FORM RENDERERS PER SERVICE
// ═════════════════════════════════════════════════════════════════════════════

// ─── 1. Pre-application Support ─────────────────────────────────────────────

const TEAL = '#14B8A6';

function PreappSelectStep({ formData, setFormData, errors }) {
  const toggle = key => {
    setFormData(prev => {
      const subs = prev.selectedSubServices.includes(key)
        ? prev.selectedSubServices.filter(s => s !== key)
        : [...prev.selectedSubServices, key];
      return { ...prev, selectedSubServices: subs };
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(TEAL)}
        >
          <ListChecks className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Select Sub-Services
          </h3>
          <p className="text-sm text-gray-500">
            Choose one or more services you need help with
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SUB_SERVICE_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const checked = formData.selectedSubServices.includes(opt.key);
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => toggle(opt.key)}
              className="flex items-center gap-3 p-3.5 rounded-xl text-left transition-all duration-200"
              style={
                checked
                  ? {
                      background: `linear-gradient(135deg, ${TEAL}0d, rgba(255,255,255,0.6))`,
                      border: `1.5px solid ${TEAL}30`,
                      boxShadow: `0 2px 12px ${TEAL}20`,
                    }
                  : {
                      ...glassCard,
                      border: '1.5px solid rgba(255,255,255,0.55)',
                    }
              }
            >
              <div
                className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all duration-200"
                style={
                  checked
                    ? {
                        background: `linear-gradient(135deg, ${TEAL}, ${TEAL}cc)`,
                        boxShadow: `0 2px 8px ${TEAL}44`,
                      }
                    : {
                        background: 'rgba(255,255,255,0.8)',
                        border: '1.5px solid #D1D5DB',
                      }
                }
              >
                {checked && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <Icon
                className="w-4 h-4 shrink-0"
                style={{ color: checked ? TEAL : '#9CA3AF' }}
              />
              <span
                className="text-sm font-medium"
                style={{ color: checked ? '#1f2937' : '#6B7280' }}
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
      <FieldError error={errors.selectedSubServices} />
    </div>
  );
}

function PreappSubDetailStep({
  serviceSlug,
  subKey,
  formData,
  setFormData,
  errors,
  currentStep,
}) {
  const details = formData.subServiceDetails[subKey] || {};
  const subErrors = errors._subDetails?.[subKey] || {};

  const update = (field, value) => {
    setFormData(prev => ({
      ...prev,
      subServiceDetails: {
        ...prev.subServiceDetails,
        [subKey]: { ...prev.subServiceDetails[subKey], [field]: value },
      },
    }));
  };
  // ===== COMPONENT =====

  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  const opt = SUB_SERVICE_OPTIONS.find(o => o.key === subKey);
  const Icon = opt?.icon || FileEdit;

  switch (subKey) {
    case 'IELTS_REGISTRATION':
    case 'PTE_REGISTRATION': {
      const centers =
        subKey === 'IELTS_REGISTRATION' ? TEST_CENTERS_BD : PTE_CENTERS_BD;
      const label = subKey === 'IELTS_REGISTRATION' ? 'IELTS' : 'PTE';
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                {label} Registration Details
              </h3>
              <p className="text-sm text-gray-500">
                Tell us your preferred test details
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preferred Test Date *</Label>
              <Input
                type="date"
                value={details.preferredTestDate || ''}
                onChange={e => update('preferredTestDate', e.target.value)}
                className={inputCls(subErrors.preferredTestDate)}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <FieldError error={subErrors.preferredTestDate} />
            </div>
            <div className="space-y-2">
              <Label>Alternate Date</Label>
              <Input
                type="date"
                value={details.alternateDate || ''}
                onChange={e => update('alternateDate', e.target.value)}
                className={inputCls(false)}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Preferred Center *</Label>
            <Select
              value={details.preferredCenter || ''}
              onValueChange={v => update('preferredCenter', v)}
            >
              <SelectTrigger
                className={inputCls(subErrors.preferredCenter)}
                style={{
                  background: `${theme.inputBg} !important`,
                  borderColor: theme.inputBorder,
                }}
              >
                <SelectValue placeholder="Select center" />
              </SelectTrigger>
              <SelectContent>
                {centers.map(c => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError error={subErrors.preferredCenter} />
          </div>
          <div className="space-y-2">
            <Label>Current Preparation Level</Label>
            <Select
              value={details.currentPreparationLevel || ''}
              onValueChange={v => update('currentPreparationLevel', v)}
            >
              <SelectTrigger
                className={inputCls(false)}
                style={{
                  background: `${theme.inputBg} !important`,
                  borderColor: theme.inputBorder,
                }}
              >
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    case 'IELTS_CLASS':
    case 'PTE_CLASS': {
      const label = subKey === 'IELTS_CLASS' ? 'IELTS' : 'PTE';
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                {label} Class Details
              </h3>
              <p className="text-sm text-gray-500">
                Your preferred class schedule
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Mode *</Label>
              <Select
                value={details.mode || ''}
                onValueChange={v => update('mode', v)}
              >
                <SelectTrigger
                  className={inputCls(subErrors.mode)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={subErrors.mode} />
            </div>
            <div className="space-y-2">
              <Label>Preferred Schedule *</Label>
              <Select
                value={details.preferredSchedule || ''}
                onValueChange={v => update('preferredSchedule', v)}
              >
                <SelectTrigger
                  className={inputCls(subErrors.preferredSchedule)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select schedule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Morning</SelectItem>
                  <SelectItem value="afternoon">Afternoon</SelectItem>
                  <SelectItem value="evening">Evening</SelectItem>
                  <SelectItem value="weekend">Weekend</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={subErrors.preferredSchedule} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Current Score (optional)</Label>
              <Input
                value={details.currentScore || ''}
                onChange={e => update('currentScore', e.target.value)}
                placeholder="e.g. 5.5"
                className={inputCls(false)}
                style={{
                  background: theme.inputBg,
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Target Score</Label>
              <Input
                value={details.targetScore || ''}
                onChange={e => update('targetScore', e.target.value)}
                placeholder="e.g. 7.0"
                className={inputCls(false)}
                style={{
                  background: theme.inputBg,
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Preferred Start Date</Label>
            <Input
              type="date"
              value={details.startDate || ''}
              onChange={e => update('startDate', e.target.value)}
              className={inputCls(false)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      );
    }
    case 'LANGUAGE_CLASS':
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Language Class Details
              </h3>
              <p className="text-sm text-gray-500">
                Tell us about the language you want to learn
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Language *</Label>
              <Select
                value={details.language || ''}
                onValueChange={v => update('language', v)}
              >
                <SelectTrigger
                  className={inputCls(subErrors.language)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {[
                    'Japanese',
                    'Korean',
                    'German',
                    'French',
                    'English',
                    'Other',
                  ].map(l => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={subErrors.language} />
            </div>
            <div className="space-y-2">
              <Label>Current Level *</Label>
              <Select
                value={details.currentLevel || ''}
                onValueChange={v => update('currentLevel', v)}
              >
                <SelectTrigger
                  className={inputCls(subErrors.currentLevel)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {[
                    'A1 - Beginner',
                    'A2 - Elementary',
                    'B1 - Intermediate',
                    'B2 - Upper Intermediate',
                    'C1 - Advanced',
                    'C2 - Proficient',
                  ].map(l => (
                    <SelectItem key={l} value={l.split(' - ')[0]}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={subErrors.currentLevel} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Mode *</Label>
              <Select
                value={details.mode || ''}
                onValueChange={v => update('mode', v)}
              >
                <SelectTrigger
                  className={inputCls(subErrors.mode)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={subErrors.mode} />
            </div>
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Select
                value={details.purpose || ''}
                onValueChange={v => update('purpose', v)}
              >
                <SelectTrigger
                  className={inputCls(false)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="study">Study Abroad</SelectItem>
                  <SelectItem value="work">Work Abroad</SelectItem>
                  <SelectItem value="immigration">Immigration</SelectItem>
                  <SelectItem value="personal">Personal Interest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    case 'SOP':
    case 'LOR': {
      const label =
        subKey === 'SOP' ? 'Statement of Purpose' : 'Letter of Recommendation';
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                {label} Details
              </h3>
              <p className="text-sm text-gray-500">
                Help us prepare your {subKey}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target University *</Label>
              <Input
                value={details.targetUniversity || ''}
                onChange={e => update('targetUniversity', e.target.value)}
                placeholder="e.g. University of Toronto"
                className={inputCls(subErrors.targetUniversity)}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <FieldError error={subErrors.targetUniversity} />
            </div>
            <div className="space-y-2">
              <Label>Target Program *</Label>
              <Input
                value={details.targetProgram || ''}
                onChange={e => update('targetProgram', e.target.value)}
                placeholder="e.g. MSc Computer Science"
                className={inputCls(subErrors.targetProgram)}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <FieldError error={subErrors.targetProgram} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Deadline *</Label>
            <Input
              type="date"
              value={details.deadline || ''}
              onChange={e => update('deadline', e.target.value)}
              className={inputCls(subErrors.deadline)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
            <FieldError error={subErrors.deadline} />
          </div>
          {subKey === 'SOP' && (
            <div className="space-y-2">
              <Label>Do you have a draft?</Label>
              <Select
                value={
                  details.hasDraft === true
                    ? 'yes'
                    : details.hasDraft === false
                      ? 'no'
                      : ''
                }
                onValueChange={v => update('hasDraft', v === 'yes')}
              >
                <SelectTrigger
                  className={inputCls(false)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {subKey === 'LOR' && (
            <div className="space-y-2">
              <Label>Number of LORs Needed</Label>
              <Select
                value={details.numberOfLORsNeeded || ''}
                onValueChange={v => update('numberOfLORsNeeded', v)}
              >
                <SelectTrigger
                  className={inputCls(false)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {['1', '2', '3', '4', '5'].map(n => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      );
    }
    case 'RESEARCH_APPLICATION':
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Research Application Details
              </h3>
              <p className="text-sm text-gray-500">
                Your research interests and targets
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Field of Research *</Label>
            <Input
              value={details.fieldOfResearch || ''}
              onChange={e => update('fieldOfResearch', e.target.value)}
              placeholder="e.g. Machine Learning, Bioinformatics"
              className={inputCls(subErrors.fieldOfResearch)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
            <FieldError error={subErrors.fieldOfResearch} />
          </div>
          <div className="space-y-2">
            <Label>Supervisor Preference</Label>
            <Input
              value={details.supervisorPreference || ''}
              onChange={e => update('supervisorPreference', e.target.value)}
              placeholder="e.g. Dr. John Smith, University of X"
              className={inputCls(false)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Research Topic</Label>
            <Textarea
              value={details.researchTopic || ''}
              onChange={e => update('researchTopic', e.target.value)}
              placeholder="Briefly describe your research interest"
              rows={3}
              className={inputCls(false)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Do you have publications?</Label>
            <Select
              value={
                details.hasPublications === true
                  ? 'yes'
                  : details.hasPublications === false
                    ? 'no'
                    : ''
              }
              onValueChange={v => update('hasPublications', v === 'yes')}
            >
              <SelectTrigger
                className={inputCls(false)}
                style={{
                  background: `${theme.inputBg} !important`,
                  borderColor: theme.inputBorder,
                }}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    case 'FREE_RESOURCE':
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Free Resource Request
              </h3>
              <p className="text-sm text-gray-500">
                What kind of resources do you need?
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Resource Type *</Label>
            <Select
              value={details.resourceType || ''}
              onValueChange={v => update('resourceType', v)}
            >
              <SelectTrigger
                className={inputCls(subErrors.resourceType)}
                style={{
                  background: `${theme.inputBg} !important`,
                  borderColor: theme.inputBorder,
                }}
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="study_material">Study Material</SelectItem>
                <SelectItem value="practice_test">Practice Test</SelectItem>
                <SelectItem value="guide">Guide / Handbook</SelectItem>
                <SelectItem value="template">Template (SOP/CV)</SelectItem>
              </SelectContent>
            </Select>
            <FieldError error={subErrors.resourceType} />
          </div>
          <div className="space-y-2">
            <Label>Specific Needs</Label>
            <Textarea
              value={details.specificNeeds || ''}
              onChange={e => update('specificNeeds', e.target.value)}
              placeholder="Describe what resources you're looking for"
              rows={3}
              className={inputCls(false)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      );
    case 'COUNSELOR_APPOINTMENT':
      return (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={stepHeaderIcon(TEAL)}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Counselor Appointment
              </h3>
              <p className="text-sm text-gray-500">
                Schedule a meeting with our counselor
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preferred Date *</Label>
              <Input
                type="date"
                value={details.preferredMeetingDate || ''}
                onChange={e => update('preferredMeetingDate', e.target.value)}
                className={inputCls(subErrors.preferredMeetingDate)}
                style={{
                  background: theme.inputBg,
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <FieldError error={subErrors.preferredMeetingDate} />
            </div>
            <div className="space-y-2">
              <Label>Preferred Time</Label>
              <Select
                value={details.preferredTimeSlot || ''}
                onValueChange={v => update('preferredTimeSlot', v)}
              >
                <SelectTrigger
                  className={inputCls(false)}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="09:00-11:00">
                    9:00 AM - 11:00 AM
                  </SelectItem>
                  <SelectItem value="11:00-13:00">
                    11:00 AM - 1:00 PM
                  </SelectItem>
                  <SelectItem value="14:00-16:00">2:00 PM - 4:00 PM</SelectItem>
                  <SelectItem value="16:00-18:00">4:00 PM - 6:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Mode *</Label>
            <Select
              value={details.mode || ''}
              onValueChange={v => update('mode', v)}
            >
              <SelectTrigger
                className={inputCls(subErrors.mode)}
                style={{
                  background: `${theme.inputBg} !important`,
                  borderColor: theme.inputBorder,
                }}
              >
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline (Dhaka Office)</SelectItem>
              </SelectContent>
            </Select>
            <FieldError error={subErrors.mode} />
          </div>
          <div className="space-y-2">
            <Label>Agenda / Topics to Discuss</Label>
            <Textarea
              value={details.agenda || ''}
              onChange={e => update('agenda', e.target.value)}
              placeholder="What would you like to discuss?"
              rows={3}
              className={inputCls(false)}
              style={{
                background: theme.inputBg, // solid white
                borderColor: theme.inputBorder,
              }}
              onFocus={e => {
                e.target.style.borderColor = accent;
                e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = theme.inputBorder;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      );
    default:
      return (
        <p className="text-sm text-gray-500">
          No additional details needed for this service.
        </p>
      );
  }
}

// ─── 2. Apply University ────────────────────────────────────────────────────

const PURPLE = '#8B5CF6';

function ApplyUniversityForm({
  serviceSlug,
  formData,
  setFormData,
  errors,
  step,
  program,
}) {
  const theme = getServiceTheme(serviceSlug, step);
  const accent = theme.accent;

  if (step === 0) {
    const programs = formData.selectedPrograms || [];

    const removeProgram = idx => {
      setFormData(prev => ({
        ...prev,
        selectedPrograms: prev.selectedPrograms.filter((_, i) => i !== idx),
      }));
    };

    const addManualProgram = () => {
      setFormData(prev => ({
        ...prev,
        selectedPrograms: [
          ...prev.selectedPrograms,
          {
            programId: `manual-${Date.now()}`,
            programName: '',
            universityName: '',
            country: '',
            intake: '',
            priority: prev.selectedPrograms.length + 1,
            source: 'manual',
          },
        ],
      }));
    };

    const updateProgram = (idx, field, value) => {
      setFormData(prev => {
        const updated = [...prev.selectedPrograms];
        updated[idx] = { ...updated[idx], [field]: value };
        return { ...prev, selectedPrograms: updated };
      });
    };
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={stepHeaderIcon(PURPLE)}
          >
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Select Programs</h3>
            <p className="text-sm text-gray-500">
              Add the programs you want to apply to
            </p>
          </div>
        </div>

        {programs.map((prog, idx) => (
          <div
            key={prog.programId || idx}
            className="rounded-2xl p-4 space-y-3 relative"
            style={glassCard}
          >
            <div className="flex items-center justify-between">
              <Badge
                variant="outline"
                className="text-xs"
                style={{
                  borderColor: `${PURPLE}30`,
                  color: PURPLE,
                  background: `${PURPLE}0a`,
                }}
              >
                Priority #{prog.priority || idx + 1}{' '}
                {prog.source === 'direct'
                  ? '- Pre-selected'
                  : prog.source === 'wishlist'
                    ? '- From Wishlist'
                    : ''}
              </Badge>
              {programs.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProgram(idx)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {prog.source === 'direct' ? (
              <div
                className="rounded-xl p-3"
                style={{
                  background: `${PURPLE}09`,
                  border: `1px solid ${PURPLE}20`,
                }}
              >
                <p className="text-sm font-semibold text-gray-900">
                  {prog.programName}
                </p>
                <p className="text-xs text-gray-600">
                  {prog.universityName} &middot; {prog.country}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Program Name *</Label>
                  <Input
                    value={prog.programName}
                    onChange={e =>
                      updateProgram(idx, 'programName', e.target.value)
                    }
                    placeholder="e.g. MSc Computer Science"
                    className={`h-9 text-sm ${inputCls(false)}`}
                    style={{
                      background: theme.inputBg,
                      borderColor: theme.inputBorder,
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = accent;
                      e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = theme.inputBorder;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">University *</Label>
                  <Input
                    value={prog.universityName}
                    onChange={e =>
                      updateProgram(idx, 'universityName', e.target.value)
                    }
                    placeholder="e.g. University of Toronto"
                    className={`h-9 text-sm ${inputCls(false)}`}
                    style={{
                      background: theme.inputBg,
                      borderColor: theme.inputBorder,
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = accent;
                      e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = theme.inputBorder;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Country</Label>
                  <Select
                    value={prog.country || ''}
                    onValueChange={v => updateProgram(idx, 'country', v)}
                  >
                    <SelectTrigger
                      className={`h-9 text-sm ${inputCls(false)}`}
                      style={{
                        background: `${theme.inputBg} !important`,
                        borderColor: theme.inputBorder,
                      }}
                    >
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Intake</Label>
                  <Input
                    value={prog.intake || ''}
                    onChange={e => updateProgram(idx, 'intake', e.target.value)}
                    placeholder="e.g. Fall 2026"
                    className={`h-9 text-sm ${inputCls(false)}`}
                    style={{
                      background: theme.inputBg,
                      borderColor: theme.inputBorder,
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = accent;
                      e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = theme.inputBorder;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addManualProgram}
          className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-200"
          style={{
            background: 'rgba(255,255,255,0.5)',
            border: `1.5px dashed ${PURPLE}30`,
            color: PURPLE,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = `${PURPLE}08`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
          }}
        >
          + Add Another Program
        </button>
        <FieldError error={errors.selectedPrograms} />
      </div>
    );
  }

  // Step 1: Application details
  const docStatus = formData.applicationDetails?.documentStatus || {};
  const updateAppDetail = (field, value) => {
    setFormData(prev => ({
      ...prev,
      applicationDetails: { ...prev.applicationDetails, [field]: value },
    }));
  };
  const updateDocStatus = (docType, value) => {
    setFormData(prev => ({
      ...prev,
      applicationDetails: {
        ...prev.applicationDetails,
        documentStatus: {
          ...prev.applicationDetails?.documentStatus,
          [docType]: value,
        },
      },
    }));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(PURPLE)}
        >
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Application Details
          </h3>
          <p className="text-sm text-gray-500">
            Document readiness and preferences
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-semibold">Document Status</Label>
        <div className="rounded-2xl overflow-hidden" style={glassCard}>
          {[
            'transcripts',
            'testScores',
            'sop',
            'lor',
            'resume',
            'passport',
          ].map((doc, i, arr) => (
            <div
              key={doc}
              className="flex items-center justify-between px-3 py-2.5"
              style={{
                borderBottom:
                  i < arr.length - 1
                    ? '1px solid rgba(255,255,255,0.6)'
                    : 'none',
              }}
            >
              <span className="text-sm text-gray-700 capitalize">
                {doc === 'testScores'
                  ? 'Test Scores'
                  : doc === 'lor'
                    ? 'LOR'
                    : doc === 'sop'
                      ? 'SOP'
                      : doc}
              </span>
              <Select
                value={docStatus[doc] || 'not_started'}
                onValueChange={v => updateDocStatus(doc, v)}
              >
                <SelectTrigger
                  className={`w-[140px] h-8 text-xs ${inputCls(false)}`}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_STATUS_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SOP Readiness</Label>
          <Select
            value={formData.applicationDetails?.sopReadiness || ''}
            onValueChange={v => updateAppDetail('sopReadiness', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="draft_ready">Draft Ready</SelectItem>
              <SelectItem value="final_ready">Final Version Ready</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Budget Range</Label>
          <Select
            value={formData.applicationDetails?.budgetRange || ''}
            onValueChange={v => updateAppDetail('budgetRange', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select budget" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="under-10000">Under $10,000</SelectItem>
              <SelectItem value="10000-20000">$10,000 - $20,000</SelectItem>
              <SelectItem value="20000-40000">$20,000 - $40,000</SelectItem>
              <SelectItem value="40000-60000">$40,000 - $60,000</SelectItem>
              <SelectItem value="over-60000">Over $60,000</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className="flex items-center gap-2.5 px-3 py-3 rounded-xl"
        style={{ background: `${PURPLE}08`, border: `1px solid ${PURPLE}20` }}
      >
        <Checkbox
          checked={formData.applicationDetails?.scholarshipInterest || false}
          onCheckedChange={v => updateAppDetail('scholarshipInterest', v)}
          id="scholarship"
        />
        <label htmlFor="scholarship" className="text-sm text-gray-700">
          I'm interested in scholarship opportunities
        </label>
      </div>

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea
          value={formData.applicationDetails?.applicationNotes || ''}
          onChange={e => updateAppDetail('applicationNotes', e.target.value)}
          placeholder="Any specific requirements or preferences..."
          rows={3}
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
    </div>
  );
}

// ─── 3. Visa & Interview ────────────────────────────────────────────────────

const ROSE = '#F43F5E';

function VisaForm({ serviceSlug, currentStep, formData, setFormData, errors }) {
  const update = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(ROSE)}
        >
          <Plane className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Visa & Interview Details
          </h3>
          <p className="text-sm text-gray-500">
            Tell us about your visa requirements
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Destination Country *</Label>
          <Select
            value={formData.destinationCountry || ''}
            onValueChange={v => update('destinationCountry', v)}
          >
            <SelectTrigger
              className={inputCls(errors.destinationCountry)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map(c => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.destinationCountry} />
        </div>
        <div className="space-y-2">
          <Label>Visa Type *</Label>
          <Select
            value={formData.visaType || ''}
            onValueChange={v => update('visaType', v)}
          >
            <SelectTrigger
              className={inputCls(errors.visaType)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {VISA_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.visaType} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>University Name</Label>
          <Input
            value={formData.universityName || ''}
            onChange={e => update('universityName', e.target.value)}
            placeholder="University name"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Program Name</Label>
          <Input
            value={formData.programName || ''}
            onChange={e => update('programName', e.target.value)}
            placeholder="Program name"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Passport Expiry</Label>
          <Input
            type="date"
            value={formData.passportExpiry || ''}
            onChange={e => update('passportExpiry', e.target.value)}
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Preferred Embassy</Label>
          <Input
            value={formData.preferredEmbassy || ''}
            onChange={e => update('preferredEmbassy', e.target.value)}
            placeholder="e.g. Dhaka"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Offer Letter Received?</Label>
          <Select
            value={
              formData.offerLetterReceived === true
                ? 'yes'
                : formData.offerLetterReceived === false
                  ? 'no'
                  : ''
            }
            onValueChange={v => update('offerLetterReceived', v === 'yes')}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Previous Visa Rejection?</Label>
          <Select
            value={
              formData.previousVisaRejection === true
                ? 'yes'
                : formData.previousVisaRejection === false
                  ? 'no'
                  : ''
            }
            onValueChange={v => update('previousVisaRejection', v === 'yes')}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className="flex items-center gap-2.5 px-3 py-3 rounded-xl"
        style={{ background: `${ROSE}08`, border: `1px solid ${ROSE}20` }}
      >
        <Checkbox
          checked={formData.needMockInterview || false}
          onCheckedChange={v => update('needMockInterview', v)}
          id="mockInterview"
        />
        <label htmlFor="mockInterview" className="text-sm text-gray-700">
          I need mock interview preparation
        </label>
      </div>

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea
          value={formData.additionalNotes || ''}
          onChange={e => update('additionalNotes', e.target.value)}
          placeholder="Any additional information..."
          rows={3}
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
    </div>
  );
}

// ─── 4. Ticket & Travel ─────────────────────────────────────────────────────

const AMBER = '#F59E0B';

const TRAVEL_TRIP_TYPES = ['One Way', 'Round Trip', 'Multi City'];
const TRAVEL_FARE_TYPES = ['Regular Fare', 'Student Fare', 'Umrah Fare'];
const TRAVELER_UI_OPTIONS = [
  '1 Traveller',
  '2 Travellers',
  '3 Travellers',
  '4 Travellers',
];
const TRAVEL_CLASS_OPTIONS = ['Economy', 'Premium Economy', 'Business'];
const BUDGET_PRESETS = [40000, 60000, 80000, 100000, 120000];

function TravelModePill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
        active
          ? 'bg-blue-600 text-white shadow-[0_14px_30px_rgba(37,99,235,0.26)]'
          : 'bg-white/75 text-slate-600 hover:bg-white hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

function TravelOptionPill({ active, children, onClick, tone = 'amber' }) {
  const activeClass =
    tone === 'blue'
      ? 'border-blue-300 bg-blue-50 text-blue-700'
      : 'border-amber-300 bg-amber-50 text-amber-700';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
        active
          ? activeClass
          : 'border-slate-200 bg-white/75 text-slate-500 hover:border-slate-300 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function TravelFieldCard({ caption, children, className = '' }) {
  return (
    <div
      className={`rounded-[24px] border border-white/60 bg-white/80 px-4 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur-md ${className}`}
    >
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
        {caption}
      </div>
      {children}
    </div>
  );
}

function formatBudgetLabel(value) {
  return `BDT ${Number(value).toLocaleString()}`;
}

function TravelForm({
  serviceSlug,
  currentStep,
  formData,
  setFormData,
  errors,
  initialUiState,
}) {
  const update = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const [tripType, setTripType] = useState('Round Trip');
  const [fareType, setFareType] = useState('Regular Fare');
  const [travellerCount, setTravellerCount] = useState('1 Traveller');
  const [cabinClass, setCabinClass] = useState('Economy');
  const [returnDate, setReturnDate] = useState(initialUiState?.returnDate || '');
  const [showUniversity, setShowUniversity] = useState(
    Boolean(formData.universityName),
  );
  const [showEmergency, setShowEmergency] = useState(
    Boolean(
      formData.emergencyContactName ||
        formData.emergencyContactPhone ||
        formData.emergencyContactRelation,
    ),
  );

  const budgetValue = useMemo(() => {
    const numeric = Number(
      String(formData.budgetRange || '').replace(/[^\d]/g, ''),
    );
    if (!numeric) return 60000;
    return Math.min(Math.max(numeric, 30000), 150000);
  }, [formData.budgetRange]);

  const inputBase =
    'h-auto border-0 bg-transparent px-0 py-0 text-base font-semibold text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0';

  const inputWithError = hasError =>
    `${inputBase} ${hasError ? 'text-red-600 placeholder:text-red-300' : ''}`;

  const handleSwap = () => {
    setFormData(prev => ({
      ...prev,
      destinationCity: prev.departureCity || '',
      departureCity: prev.destinationCity || '',
    }));
  };

  useEffect(() => {
    setReturnDate(initialUiState?.returnDate || '');
  }, [initialUiState?.returnDate]);

  return (
    <div className="space-y-5">
      <div className="relative overflow-hidden rounded-[28px] border border-amber-200/60 bg-[linear-gradient(135deg,rgba(245,158,11,0.16),rgba(255,255,255,0.9),rgba(251,191,36,0.12))] p-5 shadow-[0_24px_70px_rgba(245,158,11,0.12)] backdrop-blur-xl sm:p-6">
        <div className="absolute -top-16 right-0 h-40 w-40 rounded-full bg-amber-300/20 blur-3xl" />
        <div className="absolute -bottom-14 left-0 h-36 w-36 rounded-full bg-sky-300/15 blur-3xl" />

        <div className="relative space-y-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                <Sparkles className="h-3.5 w-3.5" />
                Fly8 Travel Planner
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg"
                  style={stepHeaderIcon(AMBER)}
                >
                  <Plane className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Build your travel request
                  </h3>
                  <p className="text-sm text-slate-500">
                    Same Fly8 workflow, redesigned like a booking engine.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {TRAVEL_FARE_TYPES.map(type => (
                <TravelOptionPill
                  key={type}
                  active={fareType === type}
                  onClick={() => setFareType(type)}
                  tone="blue"
                >
                  {type}
                </TravelOptionPill>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {TRAVEL_TRIP_TYPES.map(type => (
              <TravelModePill
                key={type}
                active={tripType === type}
                onClick={() => setTripType(type)}
              >
                {type}
              </TravelModePill>
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.2fr_auto_1.2fr]">
            <div>
              <TravelFieldCard caption="From">
                <Input
                  value={formData.departureCity || ''}
                  onChange={e => update('departureCity', e.target.value)}
                  placeholder="Departure city"
                  className={inputWithError(errors.departureCity)}
                />
                <p className="mt-2 text-sm text-slate-500">
                  Your outbound airport or city
                </p>
              </TravelFieldCard>
              <FieldError error={errors.departureCity} />
            </div>

            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={handleSwap}
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/70 bg-white/90 text-slate-600 shadow-[0_12px_32px_rgba(15,23,42,0.12)] transition-all duration-200 hover:-translate-y-0.5 hover:text-amber-600"
                aria-label="Swap route"
              >
                <ArrowLeftRight className="h-5 w-5" />
              </button>
            </div>

            <div>
              <TravelFieldCard caption="To">
                <Input
                  value={formData.destinationCity || ''}
                  onChange={e => update('destinationCity', e.target.value)}
                  placeholder="Destination city"
                  className={inputWithError(errors.destinationCity)}
                />
                <p className="mt-2 text-sm text-slate-500">
                  Where you want Fly8 to support your arrival
                </p>
              </TravelFieldCard>
              <FieldError error={errors.destinationCity} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <TravelFieldCard caption="Departure Date">
              <Input
                type="date"
                value={formData.preferredDepartureDate || ''}
                onChange={e => update('preferredDepartureDate', e.target.value)}
                className={inputBase}
              />
              <p className="mt-2 text-sm text-slate-500">Preferred departure</p>
            </TravelFieldCard>

            <TravelFieldCard caption="Return Date">
              <Input
                type="date"
                value={returnDate}
                onChange={e => setReturnDate(e.target.value)}
                className={inputBase}
              />
              <p className="mt-2 text-sm text-slate-500">
                Optional return date for planning a flexible travel window.
              </p>
            </TravelFieldCard>

            <TravelFieldCard caption="Travellers">
              <Select value={travellerCount} onValueChange={setTravellerCount}>
                <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-base font-semibold text-slate-900 shadow-none focus:ring-0">
                  <SelectValue placeholder="Select travellers" />
                </SelectTrigger>
                <SelectContent>
                  {TRAVELER_UI_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4 text-blue-500" />
                Planning-only selector
              </p>
            </TravelFieldCard>

            <TravelFieldCard caption="Class">
              <Select value={cabinClass} onValueChange={setCabinClass}>
                <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-base font-semibold text-slate-900 shadow-none focus:ring-0">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {TRAVEL_CLASS_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-sm text-slate-500">
                Cabin preference for briefing
              </p>
            </TravelFieldCard>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr_1.1fr_1.2fr]">
            <div>
              <TravelFieldCard caption="Destination Country">
                <Select
                  value={formData.destinationCountry || ''}
                  onValueChange={v => update('destinationCountry', v)}
                >
                  <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-base font-semibold text-slate-900 shadow-none focus:ring-0">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-amber-500" />
                      <SelectValue placeholder="Select country" />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(c => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-3 flex flex-wrap gap-2">
                  {COUNTRY_OPTIONS.slice(0, 5).map(country => (
                    <button
                      key={country}
                      type="button"
                      onClick={() => update('destinationCountry', country)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                        formData.destinationCountry === country
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
              </TravelFieldCard>
              <FieldError error={errors.destinationCountry} />
            </div>

            <TravelFieldCard caption="Budget Range">
              <div className="flex items-center justify-between gap-3">
                <span className="text-lg font-bold text-slate-900">
                  {formData.budgetRange || formatBudgetLabel(budgetValue)}
                </span>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  Flexible
                </span>
              </div>
              <Slider
                value={[budgetValue]}
                min={30000}
                max={150000}
                step={5000}
                onValueChange={([value]) => update('budgetRange', formatBudgetLabel(value))}
                className="mt-4"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                {BUDGET_PRESETS.map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => update('budgetRange', formatBudgetLabel(value))}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                      budgetValue === value
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {formatBudgetLabel(value)}
                  </button>
                ))}
              </div>
            </TravelFieldCard>

            <TravelFieldCard caption="Service Options">
              <div className="flex flex-wrap gap-2">
                <TravelOptionPill
                  active={Boolean(formData.airportPickupNeeded)}
                  onClick={() =>
                    update('airportPickupNeeded', !formData.airportPickupNeeded)
                  }
                >
                  Airport pickup
                </TravelOptionPill>
                <TravelOptionPill
                  active={Boolean(formData.travelInsuranceNeeded)}
                  onClick={() =>
                    update(
                      'travelInsuranceNeeded',
                      !formData.travelInsuranceNeeded,
                    )
                  }
                >
                  Travel insurance
                </TravelOptionPill>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Toggle the add-ons you want our team to prepare.
              </p>
            </TravelFieldCard>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.1fr_1.2fr]">
        <div className="rounded-[24px] border border-slate-200/80 bg-gradient-to-br from-white to-amber-50/30 p-4 shadow-sm">
          <button
            type="button"
            onClick={() => setShowUniversity(prev => !prev)}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <p className="text-sm font-semibold text-slate-900">
                University details
              </p>
              <p className="text-sm text-slate-500">
                Add this if your route depends on a specific campus or intake.
              </p>
            </div>
            <ChevronRight
              className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${
                showUniversity ? 'rotate-90' : ''
              }`}
            />
          </button>

          {showUniversity && (
            <div className="mt-4 rounded-[20px] border border-slate-200/80 bg-white/85 px-4 py-4">
              <Input
                value={formData.universityName || ''}
                onChange={e => update('universityName', e.target.value)}
                placeholder="University or campus name"
                className={inputBase}
              />
            </div>
          )}
        </div>

        <div className="rounded-[24px] border border-slate-200/80 bg-gradient-to-br from-white to-sky-50/40 p-4 shadow-sm">
          <button
            type="button"
            onClick={() => setShowEmergency(prev => !prev)}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                <Phone className="h-4.5 w-4.5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Emergency contact
                </p>
                <p className="text-sm text-slate-500">
                  Optional, but useful for arrival-day support.
                </p>
              </div>
            </div>
            <ChevronRight
              className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${
                showEmergency ? 'rotate-90' : ''
              }`}
            />
          </button>

          {showEmergency && (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-[20px] border border-slate-200/80 bg-white/85 px-4 py-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Name
                </div>
                <Input
                  value={formData.emergencyContactName || ''}
                  onChange={e => update('emergencyContactName', e.target.value)}
                  placeholder="Full name"
                  className={inputBase}
                />
              </div>
              <div className="rounded-[20px] border border-slate-200/80 bg-white/85 px-4 py-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Phone
                </div>
                <Input
                  value={formData.emergencyContactPhone || ''}
                  onChange={e => update('emergencyContactPhone', e.target.value)}
                  placeholder="+880..."
                  className={inputBase}
                />
              </div>
              <div className="rounded-[20px] border border-slate-200/80 bg-white/85 px-4 py-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Relation
                </div>
                <Input
                  value={formData.emergencyContactRelation || ''}
                  onChange={e =>
                    update('emergencyContactRelation', e.target.value)
                  }
                  placeholder="e.g. Father"
                  className={inputBase}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── 5. Find Accommodation ──────────────────────────────────────────────────

const CYAN = '#06B6D4';

function AccommodationForm({
  serviceSlug,
  currentStep,
  formData,
  setFormData,
  errors,
}) {
  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  const update = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(CYAN)}
        >
          <Home className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Accommodation Preferences
          </h3>
          <p className="text-sm text-gray-500">
            Help us find the right housing for you
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Destination City *</Label>
          <Input
            value={formData.destinationCity || ''}
            onChange={e => update('destinationCity', e.target.value)}
            placeholder="e.g. Melbourne"
            className={inputCls(errors.destinationCity)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
          <FieldError error={errors.destinationCity} />
        </div>
        <div className="space-y-2">
          <Label>Destination Country</Label>
          <Select
            value={formData.destinationCountry || ''}
            onValueChange={v => update('destinationCountry', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map(c => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>University Name</Label>
          <Input
            value={formData.universityName || ''}
            onChange={e => update('universityName', e.target.value)}
            placeholder="University name"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Move-in Date</Label>
          <Input
            type="date"
            value={formData.moveInDate || ''}
            onChange={e => update('moveInDate', e.target.value)}
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Accommodation Type *</Label>
          <Select
            value={formData.accommodationType || ''}
            onValueChange={v => update('accommodationType', v)}
          >
            <SelectTrigger
              className={inputCls(errors.accommodationType)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {ACCOMMODATION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.accommodationType} />
        </div>
        <div className="space-y-2">
          <Label>Monthly Budget *</Label>
          <Input
            value={formData.monthlyBudget || ''}
            onChange={e => update('monthlyBudget', e.target.value)}
            placeholder="e.g. 1200 CAD"
            className={inputCls(errors.monthlyBudget)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
          <FieldError error={errors.monthlyBudget} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Room Preference</Label>
          <Select
            value={formData.roomPreference || ''}
            onValueChange={v => update('roomPreference', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SINGLE">Single Room</SelectItem>
              <SelectItem value="SHARED">Shared Room</SelectItem>
              <SelectItem value="STUDIO">Studio</SelectItem>
              <SelectItem value="NO_PREFERENCE">No Preference</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Max Commute Time</Label>
          <Select
            value={formData.maxCommuteMinutes || ''}
            onValueChange={v => update('maxCommuteMinutes', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 minutes</SelectItem>
              <SelectItem value="30">30 minutes</SelectItem>
              <SelectItem value="45">45 minutes</SelectItem>
              <SelectItem value="60">60 minutes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl p-3"
        style={{ background: `${CYAN}08`, border: `1px solid ${CYAN}20` }}
      >
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={formData.furnished || false}
            onCheckedChange={v => update('furnished', v)}
            id="furnished"
          />
          <label htmlFor="furnished" className="text-sm text-gray-700">
            Furnished preferred
          </label>
        </div>
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={formData.sharedAccommodationOk || false}
            onCheckedChange={v => update('sharedAccommodationOk', v)}
            id="shared"
          />
          <label htmlFor="shared" className="text-sm text-gray-700">
            Open to roommates
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Special Requirements</Label>
        <Textarea
          value={formData.additionalNotes || ''}
          onChange={e => update('additionalNotes', e.target.value)}
          placeholder="Dietary needs, accessibility, etc."
          rows={3}
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
    </div>
  );
}

// ─── 6. Education Loan ──────────────────────────────────────────────────────

const LIME = '#84CC16';

function LoanForm({ serviceSlug, currentStep, formData, setFormData, errors }) {
  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  const update = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));
  const updateCoApp = (field, value) => {
    setFormData(prev => ({
      ...prev,
      coApplicant: { ...prev.coApplicant, [field]: value },
    }));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(LIME)}
        >
          <DollarSign className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Education Loan Details
          </h3>
          <p className="text-sm text-gray-500">
            Financial information for loan processing
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>University Name *</Label>
          <Input
            value={formData.universityName || ''}
            onChange={e => update('universityName', e.target.value)}
            placeholder="University name"
            className={inputCls(errors.universityName)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
          <FieldError error={errors.universityName} />
        </div>
        <div className="space-y-2">
          <Label>Program Name</Label>
          <Input
            value={formData.programName || ''}
            onChange={e => update('programName', e.target.value)}
            placeholder="Program name"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Destination Country</Label>
          <Select
            value={formData.destinationCountry || ''}
            onValueChange={v => update('destinationCountry', v)}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map(c => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Program Duration</Label>
          <Input
            value={formData.programDuration || ''}
            onChange={e => update('programDuration', e.target.value)}
            placeholder="e.g. 2 years"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Total Cost Estimate</Label>
          <Input
            value={formData.totalCostEstimate || ''}
            onChange={e => update('totalCostEstimate', e.target.value)}
            placeholder="e.g. 65,000 USD"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Loan Amount Needed *</Label>
          <Input
            value={formData.loanAmountNeeded || ''}
            onChange={e => update('loanAmountNeeded', e.target.value)}
            placeholder="e.g. 55,000 USD"
            className={inputCls(errors.loanAmountNeeded)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
          <FieldError error={errors.loanAmountNeeded} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Offer Letter Received?</Label>
          <Select
            value={
              formData.offerLetterReceived === true
                ? 'yes'
                : formData.offerLetterReceived === false
                  ? 'no'
                  : ''
            }
            onValueChange={v => update('offerLetterReceived', v === 'yes')}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Collateral Available?</Label>
          <Select
            value={
              formData.collateralAvailable === true
                ? 'yes'
                : formData.collateralAvailable === false
                  ? 'no'
                  : ''
            }
            onValueChange={v => update('collateralAvailable', v === 'yes')}
          >
            <SelectTrigger
              className={inputCls(false)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Has Co-Applicant? *</Label>
        <Select
          value={
            formData.hasCoApplicant === true
              ? 'yes'
              : formData.hasCoApplicant === false
                ? 'no'
                : ''
          }
          onValueChange={v => update('hasCoApplicant', v === 'yes')}
        >
          <SelectTrigger
            className={inputCls(errors.hasCoApplicant)}
            style={{
              background: `${theme.inputBg} !important`,
              borderColor: theme.inputBorder,
            }}
          >
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        <FieldError error={errors.hasCoApplicant} />
      </div>

      {formData.hasCoApplicant && (
        <div
          className="rounded-2xl p-4 space-y-3"
          style={{
            background: `linear-gradient(135deg, ${LIME}0a, rgba(255,255,255,0.5))`,
            border: `1.5px solid ${LIME}25`,
            boxShadow: `0 2px 12px ${LIME}10`,
          }}
        >
          <p className="text-sm font-semibold text-gray-800">
            Co-Applicant Details
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Relationship *</Label>
              <Input
                value={formData.coApplicant?.relationship || ''}
                onChange={e => updateCoApp('relationship', e.target.value)}
                placeholder="e.g. Father"
                className={`h-9 text-sm ${inputCls(false)}`}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Occupation *</Label>
              <Input
                value={formData.coApplicant?.occupation || ''}
                onChange={e => updateCoApp('occupation', e.target.value)}
                placeholder="e.g. Business Owner"
                className={`h-9 text-sm ${inputCls(false)}`}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Monthly Income</Label>
              <Input
                value={formData.coApplicant?.monthlyIncome || ''}
                onChange={e => updateCoApp('monthlyIncome', e.target.value)}
                placeholder="e.g. 80,000 BDT"
                className={`h-9 text-sm ${inputCls(false)}`}
                style={{
                  background: theme.inputBg, // solid white
                  borderColor: theme.inputBorder,
                }}
                onFocus={e => {
                  e.target.style.borderColor = accent;
                  e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Income Proof Type</Label>
              <Select
                value={formData.coApplicant?.incomeProofType || ''}
                onValueChange={v => updateCoApp('incomeProofType', v)}
              >
                <SelectTrigger
                  className={`h-9 text-sm ${inputCls(false)}`}
                  style={{
                    background: `${theme.inputBg} !important`,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALARY_CERTIFICATE">
                    Salary Certificate
                  </SelectItem>
                  <SelectItem value="TAX_RETURN">Tax Return</SelectItem>
                  <SelectItem value="BANK_STATEMENT">Bank Statement</SelectItem>
                  <SelectItem value="BUSINESS_LICENSE">
                    Business License
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea
          value={formData.additionalNotes || ''}
          onChange={e => update('additionalNotes', e.target.value)}
          placeholder="Any additional information (existing loans, preferred banks, etc.)"
          rows={3}
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
    </div>
  );
}

// ─── 7. Find Jobs Abroad ────────────────────────────────────────────────────

const ORANGE = '#F97316';

function JobsForm({ serviceSlug, currentStep, formData, setFormData, errors }) {
  const update = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(ORANGE)}
        >
          <Briefcase className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Job Search Details
          </h3>
          <p className="text-sm text-gray-500">
            Help us match you with opportunities
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Destination Country *</Label>
          <Select
            value={formData.destinationCountry || ''}
            onValueChange={v => update('destinationCountry', v)}
          >
            <SelectTrigger
              className={inputCls(errors.destinationCountry)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map(c => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.destinationCountry} />
        </div>
        <div className="space-y-2">
          <Label>Job Type *</Label>
          <Select
            value={formData.jobType || ''}
            onValueChange={v => update('jobType', v)}
          >
            <SelectTrigger
              className={inputCls(errors.jobType)}
              style={{
                background: `${theme.inputBg} !important`,
                borderColor: theme.inputBorder,
              }}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {JOB_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError error={errors.jobType} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Field of Interest *</Label>
          <Input
            value={formData.fieldOfInterest || ''}
            onChange={e => update('fieldOfInterest', e.target.value)}
            placeholder="e.g. Software Development"
            className={inputCls(errors.fieldOfInterest)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
          <FieldError error={errors.fieldOfInterest} />
        </div>
        <div className="space-y-2">
          <Label>University / Institution</Label>
          <Input
            value={formData.universityName || ''}
            onChange={e => update('universityName', e.target.value)}
            placeholder="Current/enrolled university"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Program / Degree</Label>
          <Input
            value={formData.enrolledProgram || ''}
            onChange={e => update('enrolledProgram', e.target.value)}
            placeholder="e.g. MSc Computer Science"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Work Experience</Label>
          <Input
            value={formData.workExperience || ''}
            onChange={e => update('workExperience', e.target.value)}
            placeholder="e.g. 2 years"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Skills (comma-separated)</Label>
        <Input
          value={
            Array.isArray(formData.skills)
              ? formData.skills.join(', ')
              : formData.skills || ''
          }
          onChange={e =>
            update(
              'skills',
              e.target.value
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
            )
          }
          placeholder="e.g. JavaScript, React, Node.js"
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>LinkedIn Profile</Label>
          <Input
            value={formData.linkedInUrl || ''}
            onChange={e => update('linkedInUrl', e.target.value)}
            placeholder="linkedin.com/in/..."
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Minimum Hourly Rate</Label>
          <Input
            value={formData.minimumHourlyRate || ''}
            onChange={e => update('minimumHourlyRate', e.target.value)}
            placeholder="e.g. 15 CAD"
            className={inputCls(false)}
            style={{
              background: theme.inputBg, // solid white
              borderColor: theme.inputBorder,
            }}
            onFocus={e => {
              e.target.style.borderColor = accent;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
            }}
            onBlur={e => {
              e.target.style.borderColor = theme.inputBorder;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl p-3"
        style={{ background: `${ORANGE}08`, border: `1px solid ${ORANGE}20` }}
      >
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={formData.needLinkedInOptimization || false}
            onCheckedChange={v => update('needLinkedInOptimization', v)}
            id="linkedin"
          />
          <label htmlFor="linkedin" className="text-sm text-gray-700">
            Need LinkedIn optimization
          </label>
        </div>
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={formData.needInterviewPrep || false}
            onCheckedChange={v => update('needInterviewPrep', v)}
            id="interviewPrep"
          />
          <label htmlFor="interviewPrep" className="text-sm text-gray-700">
            Need interview preparation
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Additional Notes</Label>
        <Textarea
          value={formData.additionalNotes || ''}
          onChange={e => update('additionalNotes', e.target.value)}
          placeholder="Any specific requirements..."
          rows={3}
          className={inputCls(false)}
          style={{
            background: theme.inputBg, // solid white
            borderColor: theme.inputBorder,
          }}
          onFocus={e => {
            e.target.style.borderColor = accent;
            e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
          }}
          onBlur={e => {
            e.target.style.borderColor = theme.inputBorder;
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
    </div>
  );
}

// ─── Review Step ────────────────────────────────────────────────────────────

function ReviewStep({ serviceSlug, formData, accent }) {
  const renderValue = val => {
    if (val === null || val === undefined || val === '')
      return <span className="text-gray-400 italic">Not provided</span>;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val))
      return val.length > 0 ? (
        val.join(', ')
      ) : (
        <span className="text-gray-400 italic">None</span>
      );
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  };

  const renderField = (label, value) => (
    <div
      key={label}
      className="flex justify-between items-start py-2 last:pb-0"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.55)' }}
    >
      <span className="text-sm text-gray-500 shrink-0 mr-4">{label}</span>
      <span className="text-sm font-medium text-gray-800 text-right">
        {renderValue(value)}
      </span>
    </div>
  );

  const renderSection = (title, icon, fields) => {
    const Icon = icon;
    return (
      <div className="rounded-2xl overflow-hidden" style={glassCard}>
        <div
          className="flex items-center gap-2 px-4 py-3 border-b"
          style={{
            background: `linear-gradient(135deg, ${accent}10, rgba(255,255,255,0.3))`,
            borderColor: 'rgba(255,255,255,0.6)',
          }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
          <span className="text-sm font-semibold text-gray-800">{title}</span>
        </div>
        <div className="px-4 py-2">{fields}</div>
      </div>
    );
  };

  if (serviceSlug === 'preapplication-support') {
    const subs = formData.selectedSubServices || [];
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={stepHeaderIcon(accent)}
          >
            <Eye className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Review Your Application
            </h3>
            <p className="text-sm text-gray-500">
              Please review before submitting
            </p>
          </div>
        </div>
        {renderSection(
          'Selected Services',
          ListChecks,
          <div className="flex flex-wrap gap-1.5 py-2">
            {subs.map(s => {
              const opt = SUB_SERVICE_OPTIONS.find(o => o.key === s);
              return (
                <Badge
                  key={s}
                  variant="secondary"
                  className="text-xs"
                  style={{
                    background: `${accent}12`,
                    color: accent,
                    border: `1px solid ${accent}25`,
                  }}
                >
                  {opt?.label || s}
                </Badge>
              );
            })}
          </div>,
        )}
        {subs.map(sub => {
          const details = formData.subServiceDetails?.[sub] || {};
          const opt = SUB_SERVICE_OPTIONS.find(o => o.key === sub);
          return (
            <div key={sub}>
              {renderSection(
                opt?.label || sub,
                opt?.icon || FileEdit,
                Object.entries(details).map(([key, val]) =>
                  renderField(
                    key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, s => s.toUpperCase()),
                    val,
                  ),
                ),
              )}
            </div>
          );
        })}
        {formData.generalNotes &&
          renderSection(
            'General Notes',
            FileText,
            renderField('Notes', formData.generalNotes),
          )}
      </div>
    );
  }

  // Generic review for other services
  const excludeKeys = [
    'selectedPrograms',
    'applicationDetails',
    'coApplicant',
    'subServiceDetails',
    'selectedSubServices',
  ];
  const mainFields = Object.entries(formData).filter(
    ([key]) => !excludeKeys.includes(key) && !key.startsWith('_'),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={stepHeaderIcon(accent)}
        >
          <Eye className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Review Your Application
          </h3>
          <p className="text-sm text-gray-500">
            Please review before submitting
          </p>
        </div>
      </div>

      {formData.selectedPrograms &&
        renderSection(
          'Selected Programs',
          GraduationCap,
          formData.selectedPrograms.map((p, i) => (
            <div
              key={i}
              className="py-2"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.55)' }}
            >
              <p className="text-sm font-medium text-gray-900">
                #{p.priority || i + 1} {p.programName}
              </p>
              <p className="text-xs text-gray-500">
                {p.universityName} {p.country && `\u00B7 ${p.country}`}
              </p>
            </div>
          )),
        )}

      {formData.applicationDetails &&
        renderSection(
          'Application Details',
          FileText,
          Object.entries(formData.applicationDetails).map(([key, val]) =>
            renderField(
              key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, s => s.toUpperCase()),
              val,
            ),
          ),
        )}

      {formData.coApplicant &&
        formData.hasCoApplicant &&
        renderSection(
          'Co-Applicant',
          Users,
          Object.entries(formData.coApplicant).map(([key, val]) =>
            renderField(
              key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, s => s.toUpperCase()),
              val,
            ),
          ),
        )}

      {mainFields.length > 0 &&
        renderSection(
          'Details',
          ClipboardList,
          mainFields.map(([key, val]) =>
            renderField(
              key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, s => s.toUpperCase()),
              val,
            ),
          ),
        )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
// ===== THEME GENERATOR =====
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getServiceTheme = (serviceSlug, step = 0) => {
  const accent = SERVICE_ACCENT[serviceSlug] || '#6366F1';
  const intensity = step * 0.02;

  return {
    accent,

    bg: `linear-gradient(145deg,
      ${hexToRgba(accent, 0.06 + intensity)} 0%,
      ${hexToRgba(accent, 0.1 + intensity)} 40%,
      ${hexToRgba(accent, 0.06 + intensity)} 100%)`,

    header: `linear-gradient(135deg,
      ${hexToRgba(accent, 0.18)} 0%,
      ${hexToRgba(accent, 0.12)} 100%)`,

    footer: `linear-gradient(135deg,
      ${hexToRgba(accent, 0.16)} 0%,
      ${hexToRgba(accent, 0.1)} 100%)`,

    border: hexToRgba(accent, 0.25),
    shadow: hexToRgba(accent, 0.25),

    inputBg: hexToRgba(accent, 0.05),
    inputBorder: hexToRgba(accent, 0.35),
    focusRing: hexToRgba(accent, 0.25),
  };
};

export default function ServiceFormModal({
  open,
  onOpenChange,
  serviceSlug,
  onSubmit,
  program,
  initialFormData,
  initialUiState,
  showRepresentativeNameField = true,
}) {
  // const accent = SERVICE_ACCENT[serviceSlug] || '#6366F1';
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [representativeName, setRepresentativeName] = useState('');

  // ─── Service-specific form state ────────────────────────────────────────
  const getInitialFormData = useCallback(() => {
    let baseData;

    switch (serviceSlug) {
      case 'preapplication-support':
        baseData = {
          selectedSubServices: [],
          subServiceDetails: {},
          generalNotes: '',
        };
        break;
      case 'apply-university': {
        baseData = {
          selectedPrograms: [],
          applicationDetails: {
            documentStatus: {},
            sopReadiness: '',
            budgetRange: '',
            scholarshipInterest: false,
            applicationNotes: '',
          },
        };
        if (program) {
          baseData.selectedPrograms = [
            {
              programId: program.programId || program._id,
              universityId: program.universityId || '',
              programName: program.programName || '',
              universityName: program.universityName || '',
              country: program.country || '',
              intake: '',
              priority: 1,
              source: 'direct',
            },
          ];
        }
        break;
      }
      case 'visa-interview':
        baseData = {
          destinationCountry: '',
          visaType: '',
          universityName: '',
          programName: '',
          passportExpiry: '',
          offerLetterReceived: '',
          previousVisaRejection: '',
          preferredEmbassy: '',
          needMockInterview: false,
          additionalNotes: '',
        };
        break;
      case 'ticket-travel':
        baseData = {
          destinationCountry: '',
          destinationCity: '',
          departureCity: '',
          preferredDepartureDate: '',
          universityName: '',
          budgetRange: '',
          airportPickupNeeded: false,
          travelInsuranceNeeded: false,
          emergencyContactName: '',
          emergencyContactPhone: '',
          emergencyContactRelation: '',
        };
        break;
      case 'accommodation':
        baseData = {
          destinationCity: '',
          destinationCountry: '',
          universityName: '',
          moveInDate: '',
          accommodationType: '',
          monthlyBudget: '',
          roomPreference: '',
          maxCommuteMinutes: '',
          furnished: false,
          sharedAccommodationOk: false,
          additionalNotes: '',
        };
        break;
      case 'education-loan':
        baseData = {
          universityName: '',
          programName: '',
          destinationCountry: '',
          programDuration: '',
          totalCostEstimate: '',
          loanAmountNeeded: '',
          offerLetterReceived: '',
          collateralAvailable: '',
          hasCoApplicant: '',
          coApplicant: {
            relationship: '',
            occupation: '',
            monthlyIncome: '',
            incomeProofType: '',
          },
          additionalNotes: '',
        };
        break;
      case 'jobs-abroad':
        baseData = {
          destinationCountry: '',
          jobType: '',
          fieldOfInterest: '',
          universityName: '',
          enrolledProgram: '',
          workExperience: '',
          skills: [],
          linkedInUrl: '',
          minimumHourlyRate: '',
          needLinkedInOptimization: false,
          needInterviewPrep: false,
          additionalNotes: '',
        };
        break;
      default:
        baseData = {};
    }

    if (!initialFormData || typeof initialFormData !== 'object') {
      return baseData;
    }

    return {
      ...baseData,
      ...initialFormData,
    };
  }, [serviceSlug, program, initialFormData]);

  const [formData, setFormData] = useState(getInitialFormData);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setErrors({});
      setRepresentativeName('');
      setFormData(getInitialFormData());
    }
  }, [open, getInitialFormData]);

  // ─── Steps config per service ───────────────────────────────────────────
  const steps = useMemo(() => {
    switch (serviceSlug) {
      case 'preapplication-support':
        return buildPreappSteps(formData.selectedSubServices || []);
      case 'apply-university':
        return [
          { key: 'programs', label: 'Programs', icon: GraduationCap },
          { key: 'details', label: 'Details', icon: FileText },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      case 'visa-interview':
        return [
          { key: 'form', label: 'Visa Details', icon: Plane },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      case 'ticket-travel':
        return [
          { key: 'form', label: 'Travel Details', icon: Globe },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      case 'accommodation':
        return [
          { key: 'form', label: 'Preferences', icon: Home },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      case 'education-loan':
        return [
          { key: 'form', label: 'Loan Details', icon: DollarSign },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      case 'jobs-abroad':
        return [
          { key: 'form', label: 'Job Details', icon: Briefcase },
          { key: 'review', label: 'Review', icon: Eye },
        ];
      default:
        return [
          { key: 'form', label: 'Details', icon: ClipboardList },
          { key: 'review', label: 'Review', icon: Eye },
        ];
    }
  }, [serviceSlug, formData.selectedSubServices]);

  const isReviewStep = currentStep === steps.length - 1;
  const isLastFormStep = currentStep === steps.length - 2;

  // ─── Validation ─────────────────────────────────────────────────────────
  const validateCurrentStep = () => {
    const errs = {};

    if (serviceSlug === 'preapplication-support') {
      if (currentStep === 0) {
        if (!formData.selectedSubServices.length)
          errs.selectedSubServices = 'Select at least one sub-service';
      } else if (!isReviewStep) {
        const subKey = steps[currentStep]?.key;
        if (subKey) {
          const details = formData.subServiceDetails[subKey] || {};
          const subErrors = {};
          const REQUIRED = {
            IELTS_REGISTRATION: ['preferredTestDate', 'preferredCenter'],
            PTE_REGISTRATION: ['preferredTestDate', 'preferredCenter'],
            IELTS_CLASS: ['mode', 'preferredSchedule'],
            PTE_CLASS: ['mode', 'preferredSchedule'],
            LANGUAGE_CLASS: ['language', 'currentLevel', 'mode'],
            SOP: ['targetUniversity', 'targetProgram', 'deadline'],
            LOR: ['targetUniversity', 'targetProgram', 'deadline'],
            RESEARCH_APPLICATION: ['fieldOfResearch'],
            FREE_RESOURCE: ['resourceType'],
            COUNSELOR_APPOINTMENT: ['preferredMeetingDate', 'mode'],
          };
          (REQUIRED[subKey] || []).forEach(field => {
            if (
              !details[field] ||
              (typeof details[field] === 'string' && !details[field].trim())
            ) {
              subErrors[field] =
                `${field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())} is required`;
            }
          });
          if (Object.keys(subErrors).length)
            errs._subDetails = { [subKey]: subErrors };
        }
      }
    } else if (serviceSlug === 'apply-university') {
      if (currentStep === 0) {
        if (!formData.selectedPrograms.length) {
          errs.selectedPrograms = 'Add at least one program';
        } else {
          for (const p of formData.selectedPrograms) {
            if (!p.programName?.trim() || !p.universityName?.trim()) {
              errs.selectedPrograms =
                'All programs must have a name and university';
              break;
            }
          }
        }
      }
    } else if (currentStep === 0) {
      const REQUIRED_MAP = {
        'visa-interview': {
          destinationCountry: 'Destination country',
          visaType: 'Visa type',
        },
        'ticket-travel': {
          destinationCountry: 'Destination country',
          destinationCity: 'Destination city',
          departureCity: 'Departure city',
        },
        accommodation: {
          destinationCity: 'Destination city',
          accommodationType: 'Accommodation type',
          monthlyBudget: 'Monthly budget',
        },
        'education-loan': {
          universityName: 'University name',
          loanAmountNeeded: 'Loan amount',
        },
        'jobs-abroad': {
          destinationCountry: 'Destination country',
          jobType: 'Job type',
          fieldOfInterest: 'Field of interest',
        },
      };
      const required = REQUIRED_MAP[serviceSlug] || {};
      Object.entries(required).forEach(([field, label]) => {
        const val = formData[field];
        if (!val || (typeof val === 'string' && !val.trim())) {
          errs[field] = `${label} is required`;
        }
      });

      if (serviceSlug === 'education-loan') {
        if (formData.hasCoApplicant === '')
          errs.hasCoApplicant = 'Please select';
        if (formData.hasCoApplicant === true) {
          if (!formData.coApplicant?.relationship?.trim())
            errs['coApplicant.relationship'] = 'Required';
          if (!formData.coApplicant?.occupation?.trim())
            errs['coApplicant.occupation'] = 'Required';
        }
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Navigation ─────────────────────────────────────────────────────────
  const handleNext = () => {
    if (!isReviewStep && !validateCurrentStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep < steps.length - 1) setCurrentStep(s => s + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const repName =
        typeof representativeName === 'string' && representativeName.trim()
          ? representativeName.trim()
          : undefined;
      await onSubmit(formData, repName);
      onOpenChange?.(false);
    } catch (err) {
      console.error('Form submit error:', err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to submit application',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render current step content ────────────────────────────────────────
  const renderStepContent = () => {
    if (isReviewStep)
      return (
        <ReviewStep
          serviceSlug={serviceSlug}
          formData={formData}
          accent={accent}
        />
      );

    switch (serviceSlug) {
      case 'preapplication-support':
        if (currentStep === 0)
          return (
            <PreappSelectStep
              serviceSlug={serviceSlug}
              currentStep={currentStep}
              formData={formData}
              setFormData={setFormData}
              errors={errors}
            />
          );
        return (
          <PreappSubDetailStep
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            subKey={steps[currentStep]?.key}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 'apply-university':
        return (
          <ApplyUniversityForm
            serviceSlug={serviceSlug}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            step={currentStep}
            program={program}
          />
        );
      case 'visa-interview':
        return (
          <VisaForm
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 'ticket-travel':
        return (
          <TravelForm
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            initialUiState={initialUiState}
          />
        );
      case 'accommodation':
        return (
          <AccommodationForm
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 'education-loan':
        return (
          <LoanForm
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 'jobs-abroad':
        return (
          <JobsForm
            serviceSlug={serviceSlug}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      default:
        return (
          <p className="text-gray-500">Form not available for this service.</p>
        );
    }
  };

  // ─── Title mapping ────────────────────────────────────────────────────
  const titles = {
    'preapplication-support': 'Pre-application Support',
    'apply-university': 'Apply to University',
    'visa-interview': 'Visa & Interview Support',
    'ticket-travel': 'Ticket & Travel Support',
    accommodation: 'Find Accommodation',
    'education-loan': 'Education Loan',
    'jobs-abroad': 'Find Jobs Abroad',
  };

  const theme = getServiceTheme(serviceSlug, currentStep);
  const accent = theme.accent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${
          serviceSlug === 'preapplication-support' ? 'max-w-3xl' : 'max-w-2xl'
        } max-h-[90vh] p-0 rounded-[28px] border-0 flex flex-col overflow-hidden`}
        style={{
          background: '#fff', // solid white base
        }}
      >
        {/* Header */}
        <div
          className="shrink-0 px-6 pt-5 pb-4 border-b"
          style={{
            background: theme.header,
            borderColor: theme.border,
          }}
        >
          <DialogTitle
            className="text-xl font-bold mb-4"
            style={{ color: accent }}
          >
            {titles[serviceSlug] || 'Service Application'}
          </DialogTitle>

          <FormStepper
            steps={steps}
            currentStep={currentStep}
            accent={accent}
          />
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-6 min-h-[320px]"
          style={{ background: '#fff' }} // solid white body
        >
          {showRepresentativeNameField && !isReviewStep && currentStep === 0 && (
            <div className="mb-5">
              <Label className="text-sm font-semibold text-gray-700">
                Representative Name (Optional)
              </Label>
              <Input
                value={representativeName}
                onChange={(e) => setRepresentativeName(e.target.value)}
                placeholder="Enter representative name (if any)"
                className={inputCls(false)}
              />
            </div>
          )}
          {renderStepContent({
            theme, // optional for input styling
          })}
        </div>

        {/* Footer */}
        <div
          className="shrink-0 px-6 py-4 flex items-center justify-between border-t"
          style={{
            background: theme.footer,
            borderColor: theme.border,
          }}
        >
          <Button
            variant="ghost"
            onClick={
              currentStep === 0 ? () => onOpenChange(false) : handlePrevious
            }
            disabled={submitting}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-200"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          {isReviewStep ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-xl px-8 text-white border-0 cursor-pointer hover:opacity-90 transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="rounded-xl px-8 text-white border-0 cursor-pointer hover:opacity-90 transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
                boxShadow: `0 6px 22px ${theme.shadow}`,
              }}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
